import { useEffect, useState } from "react";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { PageHeader, LoadingGrid, UploadButton, Field, Logo } from "@/components/common";
import { PolicyLinks } from "@/components/Footer";
import { NotificationsList } from "@/pages/Notifications";
import { Sel } from "@/components/admin-ui";
import { useBranding } from "@/context/BrandingContext";
import { api, errMsg, fileUrl } from "@/lib/api";

function useSettings() {
  const { reload } = useBranding();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const save = async (part) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", { [part]: s[part] });
      setS(data); await reload(); toast.success("Settings saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const set = (part, k) => (v) => setS({ ...s, [part]: { ...s[part], [k]: v?.target ? v.target.value : v } });
  return { s, set, save, busy };
}

const SaveBtn = ({ onClick, busy, testid }) => (
  <Button onClick={onClick} disabled={busy} className="rounded-full" data-testid={testid}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" />Save</>}</Button>
);
const ic = "rounded-xl bg-background h-11";

const IMAGES = [["logo_url", "Logo"], ["dashboard_logo_url", "Dashboard logo"], ["login_logo_url", "Login logo"], ["favicon_url", "Favicon"], ["app_icon_url", "App icon"]];

export function AdminBranding() {
  const { s, set, save, busy } = useSettings();
  if (!s) return <LoadingGrid count={3} className="space-y-4" h="h-32" />;
  const b = s.branding;
  return (
    <div className="max-w-4xl">
      <PageHeader eyebrow="Identity" title="Website branding" desc="Changes apply instantly across login, signup, dashboards, footer, browser title and favicon." />
      <div className="uc-card p-6 space-y-6" data-testid="branding-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Website name"><Input value={b.website_name} onChange={set("branding", "website_name")} className={ic} data-testid="branding-name-input" /></Field>
          <Field label="Tagline"><Input value={b.tagline} onChange={set("branding", "tagline")} className={ic} data-testid="branding-tagline-input" /></Field>
          <Field label="Primary color"><div className="flex gap-2"><input type="color" value={b.primary_color} onChange={set("branding", "primary_color")} className="h-11 w-14 rounded-xl bg-transparent border border-border" data-testid="branding-primary-color-picker" /><Input value={b.primary_color} onChange={set("branding", "primary_color")} className={ic} data-testid="branding-primary-color-input" /></div></Field>
          <Field label="Secondary color"><div className="flex gap-2"><input type="color" value={b.secondary_color} onChange={set("branding", "secondary_color")} className="h-11 w-14 rounded-xl bg-transparent border border-border" data-testid="branding-secondary-color-picker" /><Input value={b.secondary_color} onChange={set("branding", "secondary_color")} className={ic} data-testid="branding-secondary-color-input" /></div></Field>
          <Field label="Support email"><Input type="email" value={b.support_email} onChange={set("branding", "support_email")} className={ic} data-testid="branding-support-email-input" /></Field>
          <Field label="Instagram URL"><Input value={b.instagram_url} onChange={set("branding", "instagram_url")} className={ic} data-testid="branding-instagram-url-input" /></Field>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {IMAGES.map(([k, l]) => (
            <div key={k} className="rounded-xl border border-border p-4 flex items-center gap-3" data-testid={`branding-image-${k}`}>
              <div className="h-12 w-12 rounded-lg bg-secondary grid place-items-center overflow-hidden shrink-0">{b[k] ? <img src={fileUrl(b[k])} alt="" className="h-full w-full object-cover" /> : <span className="text-[10px] text-muted-foreground">None</span>}</div>
              <div className="min-w-0 space-y-1.5">
                <p className="text-xs font-medium">{l}</p>
                <div className="flex gap-2 items-center">
                  <UploadButton label="Upload" onUploaded={(r) => set("branding", k)(r.url)} testid={`branding-upload-${k}`} />
                  {b[k] && <button className="text-[11px] text-muted-foreground underline" onClick={() => set("branding", k)("")} data-testid={`branding-clear-${k}`}>Clear</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
        <SaveBtn onClick={() => save("branding")} busy={busy} testid="branding-save-button" />
      </div>
      <div className="uc-card p-6 mt-6"><p className="eyebrow mb-4">Live header preview</p><Logo /></div>
    </div>
  );
}

export function AdminPrivacy() {
  const { s, set, save, busy } = useSettings();
  if (!s) return <LoadingGrid count={2} className="space-y-4" h="h-32" />;
  const p = s.privacy;
  return (
    <div className="max-w-2xl">
      <PageHeader eyebrow="Legal" title="Privacy policy" desc="Link externally hosted documents (e.g. GitHub Pages). Shown on login, signup, footer and settings." />
      <div className="uc-card p-6 space-y-5" data-testid="privacy-form">
        <label className="flex items-center gap-3 text-sm"><Switch checked={p.privacy_enabled} onCheckedChange={set("privacy", "privacy_enabled")} data-testid="privacy-enabled-switch" />Show Privacy Policy link</label>
        <Field label="Privacy Policy URL"><Input value={p.privacy_policy_url} onChange={set("privacy", "privacy_policy_url")} placeholder="https://" className={ic} data-testid="privacy-url-input" /></Field>
        <Field label="Terms & Conditions URL"><Input value={p.terms_url} onChange={set("privacy", "terms_url")} placeholder="https://" className={ic} data-testid="terms-url-input" /></Field>
        <SaveBtn onClick={() => save("privacy")} busy={busy} testid="privacy-save-button" />
      </div>
    </div>
  );
}

export function AdminSettings() {
  const { s, set, save, busy } = useSettings();
  if (!s) return <LoadingGrid count={2} className="space-y-4" h="h-32" />;
  const a = s.admin;
  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader eyebrow="Configuration" title="Settings" desc="Website name, logo, colours and policy links live under Branding and Privacy Policy." />
      <div className="uc-card p-6 space-y-5" data-testid="settings-form">
        <Field label="Support contact (phone / WhatsApp)"><Input value={a.support_contact} onChange={set("admin", "support_contact")} className={ic} data-testid="settings-support-contact-input" /></Field>
        <Field label="Theme"><Sel value={a.theme} onChange={set("admin", "theme")} options={[["dark", "Dark premium"]]} testid="settings-theme-select" className="bg-background" /></Field>
        <p className="eyebrow pt-2">Notifications</p>
        {[["notify_channel_posts", "Notify creators when a channel post is published"], ["notify_deal_updates", "Notify creators on deal updates"], ["notify_payment_updates", "Notify creators on payment updates"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-3 text-sm"><Switch checked={!!a[k]} onCheckedChange={set("admin", k)} data-testid={`settings-${k}`} />{l}</label>
        ))}
        <p className="eyebrow pt-2">Channel</p>
        <label className="flex items-center gap-3 text-sm"><Switch checked={!!a.channel_enabled} onCheckedChange={set("admin", "channel_enabled")} data-testid="settings-channel-enabled" />Channel visible to creators</label>
        <SaveBtn onClick={() => save("admin")} busy={busy} testid="settings-save-button" />
      </div>
      <div className="uc-card p-6 text-xs text-muted-foreground"><p className="eyebrow mb-3">Policy links</p><PolicyLinks /></div>
    </div>
  );
}

function SendNotification() {
  return (
    <div className="uc-card p-5 text-sm text-muted-foreground mb-8">
      To notify creators, use <a href="/admin/broadcasts" className="text-primary underline" data-testid="goto-broadcasts-link">Broadcast Messages</a> (all, a category, or selected creators) or the <span className="text-foreground">Notify</span> button on any creator's profile. Deal, payment, channel and account events notify creators automatically.
    </div>
  );
}

export function AdminNotifications() {
  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow="Inbox" title="Notifications" desc="Signups, deal responses, content submissions and support messages." />
      <SendNotification />
      <NotificationsList embedded />
    </div>
  );
}
